export interface FilterGroupConfig {
  key: string;
  label: string;
  options: string[];
}

interface FilterSidebarProps {
  groups: FilterGroupConfig[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onReset: () => void;
  open: boolean;
  onClose: () => void;
}

function Options({
  group,
  value,
  onChange,
}: {
  group: FilterGroupConfig;
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  return (
    <div>
      <h4 className="text-sm font-medium text-foreground-800 mb-3">{group.label}</h4>
      <div className="flex flex-wrap gap-2">
        {group.options.map((option) => {
          const active = option === value;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(group.key, option)}
              className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                active
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PanelBody({
  groups,
  values,
  onChange,
  onReset,
}: Omit<FilterSidebarProps, "open" | "onClose">) {
  return (
    <div className="space-y-7">
      {groups.map((group) => (
        <Options
          key={group.key}
          group={group}
          value={values[group.key]}
          onChange={onChange}
        />
      ))}
      <button
        type="button"
        onClick={onReset}
        className="w-full px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
      >
        <i className="ri-refresh-line" />
        重置筛选条件
      </button>
    </div>
  );
}

export default function FilterSidebar({
  groups,
  values,
  onChange,
  onReset,
  open,
  onClose,
}: FilterSidebarProps) {
  return (
    <>
      <aside className="hidden lg:block w-64 shrink-0">
        <div className="sticky top-24 rounded-lg bg-background-100 border border-background-200 p-5">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-8 h-8 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-equalizer-line" />
            </span>
            <span className="font-medium text-foreground-950">筛选条件</span>
          </div>
          <PanelBody
            groups={groups}
            values={values}
            onChange={onChange}
            onReset={onReset}
          />
        </div>
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-[60] flex">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={onClose}
            aria-hidden="true"
          />
          <div className="relative ml-auto w-[86%] max-w-sm h-full bg-background-50 overflow-y-auto p-5 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <span className="font-medium text-foreground-950 flex items-center gap-2">
                <i className="ri-equalizer-line text-primary-500" />
                筛选条件
              </span>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={onClose}
                className="w-9 h-9 rounded-full hover:bg-background-100 flex items-center justify-center text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <PanelBody
              groups={groups}
              values={values}
              onChange={onChange}
              onReset={onReset}
            />
          </div>
        </div>
      )}
    </>
  );
}